import { createContext, useContext, useState, useEffect } from 'react';
import {
  getBifrost,
  configureBifrost,
} from '@cars24/omniauth/dist/bifrost-core/bifrost-core';
import { bifrostConfig } from '../config/bifrost.config';
import store from '../store';
import { uiRules } from '../store/actions/inventoryManagement/inventoryManagement.action';
import { LOCAL_STORAGE_KEYS } from '../utils/constants/values.constants';
import CountryNotAssignedModal from '../components/CountryNotAssignedModal';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [bifrost] = useState(() => {
    try {
      return getBifrost();
    } catch {
      return configureBifrost(bifrostConfig);
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem('accessToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('userProfile');
    return stored ? JSON.parse(stored) : null;
  });
  const [isInitialized, setIsInitialized] = useState(() => {
    // Already initialized if no token (guest) or user profile already exists
    const hasToken = !!localStorage.getItem('accessToken');
    const hasUser = !!localStorage.getItem('userProfile');
    const hasCountry = !!localStorage.getItem('country');
    return !hasToken || (hasUser && hasCountry);
  });
  const [showCountryModal, setShowCountryModal] = useState(false);

  const login = (accessToken, refreshToken, sessionId) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('sessionId', sessionId);

    setToken(accessToken);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    const sessionId = localStorage.getItem('sessionId');
    const accessToken = localStorage.getItem('accessToken');

    if (sessionId && accessToken) {
      try {
        await bifrost.logout({ sessionId, accessToken });
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userData');
    localStorage.removeItem('country');
    localStorage.removeItem('vehicleType');
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_GROUPS);

    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setShowCountryModal(false);

    window.location.href = '/';
  };

  const handleCountryModalLogout = () => {
    logout();
  };

  useEffect(() => {
    if (token && !user) {
      const fetchUserProfile = async () => {
        try {
          const response = await fetch(
            `${bifrostConfig.userProfileBaseUrl}/api/v1/user`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            const profile = {
              name: `${data.data.first_name || ''} ${
                data.data.last_name || ''
              }`.trim(),
              email: data.data.email,
              firstName: data.data.first_name,
              lastName: data.data.last_name,
              preferred_username: data.data.email, // For backward compatibility
            };

            const user_data_ums = {
              ...profile,
              country: data.data.country,
              vehicleType: Array.isArray(data.data.vehicleType)
                ? data.data.vehicleType
                : ['CAR'],
            };

            const userGroups = data.data?.roles || [];

            setUser(profile);
            localStorage.setItem('userProfile', JSON.stringify(profile));
            localStorage.setItem('userData', JSON.stringify(user_data_ums));
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.USER_GROUPS,
              JSON.stringify(userGroups)
            );

            const assignedCountries = Array.isArray(data.data.country)
              ? data.data.country
              : [];
            const countryCode = assignedCountries[0];

            if (countryCode) {
              localStorage.setItem('country', countryCode);
              setIsInitialized(true);
              setTimeout(() => {
                store.dispatch(uiRules());
              }, 0);
            } else {
              // No country assigned - show modal
              setShowCountryModal(true);
              setIsInitialized(true);
            }
          } else {
            // Response not OK - still mark as initialized to unblock UI
            setIsInitialized(true);
          }
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.USER_GROUPS,
            JSON.stringify([])
          );
          setIsInitialized(true);
        }
      };

      fetchUserProfile();
    }
  }, [token, user]);

  return (
    <AuthContext.Provider
      value={{
        bifrost,
        isAuthenticated,
        isInitialized,
        token,
        user,
        login,
        logout,
        setUser,
      }}
    >
      {children}
      {showCountryModal && (
        <CountryNotAssignedModal onLogout={handleCountryModalLogout} />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const FILE_VERSION = process.env.REACT_APP_FILE_VERSION;
